import { IS_SERVER } from '@/renderer/utils/ssr';

/**
 * Shared home-screen app-icon badge aggregator (issue #1347).
 *
 * The badge (via the Badging API `navigator.setAppBadge` / `clearAppBadge`) is a
 * single OS-level number, but we have multiple independent unread sources feeding
 * it — support-chat unreads and release-center unreads. If each source wrote the
 * badge directly they'd clobber each other (whichever updated last would win),
 * which is why support-chat notifications were getting masked by the release
 * center count and going unseen.
 *
 * Every unread source reports its current count here keyed by a stable id. The
 * badge is then set to the SUM of all sources, and only cleared once every source
 * is at zero. The Badging API is feature-detected and PWA/installed-only, so this
 * is a no-op (never throws) on browsers/contexts that don't support it.
 */

const counts = new Map<string, number>();

function applyBadge() {
  // Badging API only exists in secure, installed/PWA contexts. Guard it so we
  // never throw where it's unsupported (plain browser tabs, SSR, etc.).
  if (IS_SERVER || typeof navigator === 'undefined' || !('setAppBadge' in navigator)) {
    return;
  }

  let total = 0;
  for (const value of counts.values()) {
    total += value;
  }

  if (total <= 0) {
    navigator.clearAppBadge?.();
  } else {
    navigator.setAppBadge?.(total);
  }
}

/** Report the current unread count for a given source and refresh the badge. */
export function setBadgeSource(key: string, count: number) {
  counts.set(key, Math.max(count, 0));
  applyBadge();
}

/** Remove a source (e.g. on unmount) and refresh the badge from the remainder. */
export function clearBadgeSource(key: string) {
  if (counts.delete(key)) {
    applyBadge();
  }
}
